import { useState } from "react";

/**
 * Resets the table to page 1 when `resetKey` changes, skipping the first ready
 * snapshot so a restored page is not wiped on mount.
 *
 * Resets during render (not in an effect) so cursor page-walk hooks that run in
 * layout effects see page 1 in the same commit as a filter/search change.
 *
 * ## Contract
 *
 * - **`resetKey` must be stable across renders** when inputs have not changed
 *   (e.g. memoized filter params with sorted arrays before `JSON.stringify`).
 *   An unstable key triggers a render-loop and React throws "Too many re-renders."
 * - **`setPage` must come from state owned in the same component** (or a child
 *   that renders this hook). Calling a parent's setter during render is invalid.
 * - Pass **`ready: false`** until upstream filter inputs are settled (e.g.
 *   `useResolveTimeFilterZone` is no longer `pending`) so a late key change
 *   does not wipe list restore.
 *
 * Strict Mode re-runs this hook without unmounting. Only reset when the key
 * actually changes after the first ready snapshot.
 */
export const useResetTablePageOnChange = (
	setPage: (page: number) => void,
	resetKey: unknown,
	ready = true,
): void => {
	const [snapshot, setSnapshot] = useState<{
		hasSeeded: boolean;
		previousKey: unknown;
	}>({ hasSeeded: false, previousKey: undefined });

	if (!ready) {
		return;
	}

	if (!snapshot.hasSeeded) {
		setSnapshot({ hasSeeded: true, previousKey: resetKey });
		return;
	}

	if (Object.is(snapshot.previousKey, resetKey)) {
		return;
	}

	setSnapshot({ hasSeeded: true, previousKey: resetKey });
	setPage(1);
};
